/**
 * inputUiInfo key recording WHICH input a step's last test ran with. Changing
 * a value after the test (another form, another spreadsheet) leaves the old
 * sample data in place: without this stamp the AI Builder keeps trusting
 * evidence that describes a configuration the step no longer has.
 */
export const AI_BUILDER_TESTED_INPUT = 'aiBuilderTestedInput'

/**
 * Bound to the test's lastTestDate: a test that wrote no stamp moves
 * lastTestDate on, and the stamp then no longer speaks for the evidence.
 */
export type TestedInputStamp = {
    testedAt: string
    inputHash: string
}

type StampedStep = {
    input?: unknown
    inputUiInfo?: { lastTestDate?: string } & Record<string, unknown>
}

const FNV_OFFSET_BASIS = 0x811c9dc5
const FNV_PRIME = 0x01000193
const HEX_RADIX = 16

const isEmptyValue = (value: unknown): boolean =>
    value === null || value === undefined || value === ''

/**
 * Key order and empty values carry no meaning: the panel form materialises
 * unset props as undefined, null or '' without changing what the step does.
 */
const canonicalize = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(canonicalize)
    if (typeof value !== 'object' || value === null) return value
    const record = value as Record<string, unknown>
    const canonical: Record<string, unknown> = {}
    for (const key of Object.keys(record).sort()) {
        if (isEmptyValue(record[key])) continue
        canonical[key] = canonicalize(record[key])
    }
    return canonical
}

/** Whether two inputs do the same thing, by the canonical form the stamp hashes */
export const isSameInput = (left: unknown, right: unknown): boolean => {
    const canonicalText = (value: unknown): string | undefined =>
        isEmptyValue(value) ? undefined : JSON.stringify(canonicalize(value))
    return canonicalText(left) === canonicalText(right)
}

/** A hash, not a copy: the input may hold long texts the stamp must not double */
const hashInput = (input: unknown): string => {
    const text = JSON.stringify(canonicalize(input ?? {})) ?? ''
    let hash = FNV_OFFSET_BASIS
    for (let index = 0; index < text.length; index++) {
        hash ^= text.charCodeAt(index)
        hash = Math.imul(hash, FNV_PRIME)
    }
    return (hash >>> 0).toString(HEX_RADIX)
}

export const stampTestedInput = (
    input: unknown,
    testedAt: string,
): TestedInputStamp => ({
    testedAt,
    inputHash: hashInput(input),
})

const readStamp = (
    inputUiInfo: Record<string, unknown> | undefined,
): TestedInputStamp | null => {
    const stamp = inputUiInfo?.[AI_BUILDER_TESTED_INPUT]
    if (typeof stamp !== 'object' || stamp === null) return null
    const { testedAt, inputHash } = stamp as Record<string, unknown>
    if (typeof testedAt !== 'string' || typeof inputHash !== 'string') {
        return null
    }
    return { testedAt, inputHash }
}

/**
 * True only when the stamp provably describes the last test AND the input
 * has changed since. No stamp, or a stamp from an earlier test, proves
 * nothing — the evidence is trusted as before.
 */
export const isTestEvidenceStale = (step: StampedStep): boolean => {
    const stamp = readStamp(step.inputUiInfo)
    if (!stamp || stamp.testedAt !== step.inputUiInfo?.lastTestDate) {
        return false
    }
    return stamp.inputHash !== hashInput(step.input)
}
